import React, { useContext, useEffect, useState } from "react";
import { Input, Button, Form, Statistic, App, Space, Flex } from "antd";
import { Helmet } from 'react-helmet-async';
import { FaLock, FaShieldAlt, FaEnvelope, FaMobileAlt } from 'react-icons/fa';
import { Regex, ActionType, API, Enum } from "../resources/constants";
import language from "../resources/languages/fa_IR";

// Interfaces
import { AxiosError } from "../interfaces/axios"

// Contexts
import GlobalContext from "../contexts/global";
import AxiosContext from "../contexts/axios";

// Commons
import Captcha from '../commons/captcha'

const { Countdown } = Statistic
const { Pages: { Username: UsernameLanguage }, Validation, Message: MessageLanguage, Share } = language
const { isNumber, isMobile, isEmail, isMobileOrEmail } = Regex
const { SET_USER } = ActionType
const { USER_MANAGEMENT_AUTH_OTP_CODE, USER_MANAGEMENT_PROFILE_UPDATE_USERNAME, USER_MANAGEMENT_PROFILE_MY_ACCOUNT } = API
const { Message, UsernameType } = Enum

export default function Username({ type }: { type: string } ) {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const [countdown, setCountdown] = useState<boolean>(false)
    const [otpSecondsLeft, setOTPSecondsLeft] = useState<number>(0)
    const { meta, user, dispatch } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
        form.setFieldsValue({ ...user, mobile: user?.mobile ? '0' + user.mobile : undefined})
    }, []);

    const onFinish = ({ mobile, email, password }: { mobile?: string, email?: string, password: string }) => {
        axios.patch(USER_MANAGEMENT_PROFILE_UPDATE_USERNAME, { mobile: mobile ? mobile.substring(1) : undefined, email, password }).then((response: {}) => {
            axios.get(USER_MANAGEMENT_PROFILE_MY_ACCOUNT).then((response: { data: {} }) => {
                const { data: user } = response
                dispatch({ type: SET_USER , payload: { user }})
                setCountdown(false)
                form.resetFields(['password', 'captcha'])
                message.success(MessageLanguage[Message.CHANGE_USERNAME]).then(response => response)
            })
        }, async (error: AxiosError) => {
            const { response: { data } } = error;
            setCountdown(false)
            form.resetFields(['password', 'captcha'])
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
        })
    };


  return (
        <>
          { meta?.title && (
            <Helmet>
              <title>
                {
                  // @ts-ignore
                  UsernameLanguage[type] + ' - ' + meta.title
                }
              </title>
            </Helmet>
          )
          }
          <Form form={form} name="username" autoComplete="off" onFinish={onFinish} style={{ maxWidth: 400, margin: 'auto' }}>
            <Space direction="vertical" size="middle" style={{width:'100%'}}>
              <Form.Item
                name={type}
                hasFeedback
                rules={[
                  // @ts-ignore
                  { required: true, message: `${UsernameLanguage[type]} ${Validation.required}` },
                  type === UsernameType.Mobile ? { pattern: isMobile, message: Validation.mobile } : type === UsernameType.Email ? { pattern: isEmail, message: Validation.email } : {},
                ]}
              >
                <Input prefix={type === UsernameType.Mobile ? <FaMobileAlt size={15} /> : type === UsernameType.Email ? <FaEnvelope size={15} /> : null} placeholder={
                  // @ts-ignore
                  UsernameLanguage[type]
                } />
              </Form.Item>
              <Flex gap="small" justify={'space-between'}>
                <Form.Item
                  name="password"
                  hasFeedback
                  style={{width:'100%'}}
                  rules={[
                    { required: true, message: `${UsernameLanguage.oneTimeCode} ${Validation.required}` },
                    { pattern: isNumber, message: `${UsernameLanguage.oneTimeCode} ${Validation.pattern}` },
                    { len: 5, message: `${UsernameLanguage.oneTimeCode} 5 ${Validation.length}` },
                  ]}
                >
                  <Input type="tel" prefix={<FaLock size={15} />} maxLength={5} placeholder={UsernameLanguage.oneTimeCode} />
                </Form.Item>
                {
                  !countdown ? (
                    <Form.Item shouldUpdate>
                      {() => (
                        <Button type="primary" disabled={!form.getFieldValue(type) || !form.getFieldValue('captcha') || !!form.getFieldsError([type, 'captcha']).filter(({ errors }) => errors.length).length} ghost block style={{width:'150px'}} onClick={() => {
                          axios.post(USER_MANAGEMENT_AUTH_OTP_CODE, { username: isMobile.test(form.getFieldValue(type)) ? form.getFieldValue(type).substring(1) : form.getFieldValue(type), captcha: form.getFieldValue('captcha') }).then((response: { data: number }) => {
                            const { data: otpSecondsLeft } = response
                            setOTPSecondsLeft(otpSecondsLeft)
                            form.resetFields(['password', 'captcha'])
                            message.success(MessageLanguage[Message.VERIFY_CODE_SENT]).then(response => response )
                            setCountdown(true)
                          }, (error: AxiosError) => {
                            const { response: { data } } = error;
                            form.resetFields(['password', 'captcha'])
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response )
                          })
                        }}>
                          { UsernameLanguage.receiveCode }
                        </Button>
                      )}
                    </Form.Item>
                  ) : (
                    <Form.Item>
                      <Button type="primary" disabled ghost block style={{width:'150px'}}>
                        <Countdown value={Date.now() + Number(otpSecondsLeft) * 1000} format="ss" onFinish={() => {
                          setCountdown(false)
                        }} />
                      </Button>
                    </Form.Item>
                  )
                }
              </Flex>
              <Form.Item
                name="captcha"
                hasFeedback
                rules={[
                  { required: true, message: `${UsernameLanguage.captcha} ${Validation.required}` },
                  { pattern: isNumber, message: `${UsernameLanguage.captcha} ${Validation.pattern}` },
                  { len: 4, message: `${UsernameLanguage.captcha} 4 ${Validation.length}` },
                ]}
              >
                <Input type="tel" prefix={<FaShieldAlt size={15} />} maxLength={4} placeholder={UsernameLanguage.captcha} addonAfter={<Captcha />} />
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    type="primary"
                    block
                    htmlType="submit"
                    disabled={!form.getFieldValue(type) || !form.getFieldValue('password') || !form.getFieldValue('captcha') || !!form.getFieldsError().filter(({ errors }) => errors.length).length}
                  >
                    { Share.edit }
                  </Button>
                )}
              </Form.Item>
            </Space>
          </Form>
        </>
    )
}
